import * as React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { useMyAccess } from "@/hooks/useMyAccess";
import { ADMIN_SECTION_HOME, ADMIN_SECTION_KEYS } from "@/lib/areas/registry";

interface AreaGuardProps {
  area: "admin" | "dev" | "crm";
  /**
   * Admin section this subtree belongs to (3-level Sync Center design).
   * When set, the user must also pass `canSection(area, section)`; a blocked
   * section redirects to the first allowed one (or out of the area). Sections
   * are fail-open: old BEs / users without admin:section:* roles see all.
   */
  section?: string;
  /**
   * CP module entitlement required by this route (registry
   * ADMIN_ROUTE_ENTITLEMENTS): the route renders only when the org has the
   * module enabled AND the user holds access on it (`/me/access`). Blocked =>
   * redirect to the area root (NOT to a section home: section homes can be
   * entitlement-gated themselves and would loop). Uniform replacement for the
   * per-page checks (AvesSyncPage) and the missing ones (Pricing/Practices).
   */
  entitlement?: string;
  children: React.ReactNode;
}

/**
 * Unified area gating (replaces the realm-role guards): an area is accessible
 * when the ORG has the area module entitled (HERIKA_ADMIN / HERIKA_DEV in the
 * controlplane) AND the user holds the area client-role (area:admin/area:dev),
 * both served by `GET /me/access`.
 */
export function AreaGuard({ area, section, entitlement, children }: AreaGuardProps) {
  const { initialized, isAuthenticated } = useAuth();
  const { canArea, canSection, moduleByCode, isLoading } = useMyAccess();

  if (!initialized) return null;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (isLoading) return null; // avoid a redirect flash while access loads
  if (!canArea(area)) return <Navigate to="/" replace />;
  if (section && !canSection(area, section)) {
    const fallback = ADMIN_SECTION_KEYS.find((k) => canSection(area, k));
    return <Navigate to={fallback ? ADMIN_SECTION_HOME[fallback] : "/"} replace />;
  }
  if (entitlement) {
    const module = moduleByCode(entitlement);
    if (!(module && module.org_enabled && module.user_has_access)) {
      return <Navigate to={`/${area}`} replace />;
    }
  }

  return <>{children}</>;
}
